import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from './axiosConfig';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Button,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Avatar,
  LinearProgress
} from '@mui/material';

import HomeIcon from '@mui/icons-material/Home';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentsIcon from '@mui/icons-material/Payments';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';


const RentalDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [rentals, setRentals] = useState([]);
  const [selectedRental, setSelectedRental] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showNewRenterDialog, setShowNewRenterDialog] = useState(false);
  const [editRenterData, setEditRenterData] = useState(null);

  const isAdmin = user?.role === 'ACCOUNT_OWNER' || user?.role === 'ACCOUNT_MANAGER';

  const fetchDocuments = async (rentalId) => {
    if (!rentalId) return;
    try {
      const response = await api.get(`/rentals/documents/${rentalId}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchInvoices = async (rentalId) => {
    if (!rentalId) return;
    try {
      const response = await api.get(`/rentals/invoices/${rentalId}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await api.get('/rentals/data');
        
        if (user.role === 'RENTAL_USER') {
          setRentals([response.data]);
          setSelectedRental(response.data);
          
          if (response.data?.id) {
            await Promise.all([
              fetchDocuments(response.data.id),
              fetchInvoices(response.data.id)
            ]);
          }
        } else {
          setRentals(response.data);
          if (response.data?.length > 0) {
            setSelectedRental(response.data[0]);
            await Promise.all([
              fetchDocuments(response.data[0].id),
              fetchInvoices(response.data[0].id)
            ]);
          }
        }
      } catch (error) {
        console.error('Error loading rental data:', error);
        setError('Error loading rental data');
        setSnackbar({
          open: true,
          message: 'Error fetching rental data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.role]);

  const handleRentalChange = (event) => {
    const rental = rentals.find(r => r.id === event.target.value);
    setSelectedRental(rental);
    if (rental?.id) {
      fetchDocuments(rental.id);
      fetchInvoices(rental.id);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedRental) return;

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Please upload a PDF, JPEG, PNG, or Word document',
        severity: 'error'
      });
      return;
    }

    const formData = new FormData();
    formData.append('document', file);
    formData.append('rental_id', selectedRental.id);

    try {
      const response = await api.post('/rentals/upload_document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'Document uploaded successfully',
          severity: 'success'
        });
        await fetchDocuments(selectedRental.id);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error uploading document',
        severity: 'error'
      });
    }
  };

  const handleEditRenter = (rental) => {
    setEditRenterData(rental);
    setShowNewRenterDialog(true);
  };

  const handleSaveRenter = async (renterData) => {
    try {
      const optimisticRentals = rentals.map(rental => 
        rental.id === renterData.id ? { ...rental, ...renterData } : rental
      );
      if (!renterData.id) {
        optimisticRentals.push({ ...renterData, id: 'temp-' + Date.now() });
      }
      setRentals(optimisticRentals);
      setShowNewRenterDialog(false);
      setEditRenterData(null);

      const response = await api.post('/rentals/save', renterData);
      if (response.data) {
        const dataResponse = await api.get('/rentals/data');
        setRentals(dataResponse.data);
        
        setSnackbar({
          open: true,
          message: `Renter ${editRenterData ? 'updated' : 'added'} successfully`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error saving renter:', error);
      const dataResponse = await api.get('/rentals/data');
      setRentals(dataResponse.data);
      
      setSnackbar({
        open: true,
        message: 'Error saving renter',
        severity: 'error'
      });
    }
  };

  // Calculate stats
  const pendingDocs = documents.filter(d => d.status === 'PENDING').length;
  const unpaidInvoices = invoices.filter(i => i.status !== 'PAID').length;
  const nextInvoiceDue = invoices.find(i => i.status !== 'PAID');

  // Stat Card Component
  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary', action }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color === 'success' ? '#2e7d32' : color === 'warning' ? '#ed6c02' : color === 'error' ? '#d32f2f' : '#1976d2'} 0%, ${color === 'success' ? '#1b5e20' : color === 'warning' ? '#e65100' : color === 'error' ? '#c62828' : '#1565c0'} 100%)`,
        color: 'white'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            <Icon sx={{ fontSize: 32 }} />
          </Avatar>
        </Box>
      </CardContent>
      {action && (
        <CardActions sx={{ pt: 0 }}>
          {action}
        </CardActions>
      )}
    </Card>
  );

  // Info Card Component
  const InfoCard = ({ icon: Icon, title, children }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
            <Icon sx={{ color: 'primary.main' }} />
          </Avatar>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight="500">{value || '-'}</Typography>
    </Box>
  );

  // Overview Tab
  const RentalOverview = () => (
    <Box>
      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PaymentsIcon}
            title="Monthly Rent"
            value={`£${selectedRental?.rentCost || 0}`}
            subtitle={selectedRental?.rentPeriod || 'Monthly'}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DescriptionIcon}
            title="Documents"
            value={documents.length}
            subtitle={pendingDocs > 0 ? `${pendingDocs} pending review` : 'All up to date'}
            color={pendingDocs > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={ReceiptIcon}
            title="Invoices"
            value={invoices.length}
            subtitle={unpaidInvoices > 0 ? `${unpaidInvoices} unpaid` : 'All paid'}
            color={unpaidInvoices > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={CalendarTodayIcon}
            title="Rental Started"
            value={selectedRental?.dateRentalCommenced ? 
              new Date(selectedRental.dateRentalCommenced).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : 
              '-'
            }
            subtitle={selectedRental?.active ? 'Active tenancy' : 'Inactive'}
            color={selectedRental?.active ? 'success' : 'error'}
          />
        </Grid>
      </Grid>

      {/* Detail Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <InfoCard icon={HomeIcon} title="Property Details">
            <InfoRow label="Salon" value={selectedRental?.address1} />
            <InfoRow label="Address" value={[selectedRental?.address2, selectedRental?.address3].filter(Boolean).join(', ')} />
            <InfoRow label="Postcode" value={selectedRental?.postcode} />
            <InfoRow 
              label="Status" 
              value={
                <Chip 
                  label={selectedRental?.active ? 'Active' : 'Inactive'}
                  color={selectedRental?.active ? 'success' : 'error'}
                  size="small"
                />
              } 
            />
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoCard icon={PersonIcon} title="Contact Information">
            <InfoRow label="Tenant Name" value={selectedRental?.name} />
            <InfoRow label="Phone" value={selectedRental?.phoneNumber} />
            <InfoRow label="Emergency Contact" value={selectedRental?.emergencyContactName} />
            <InfoRow label="Emergency Phone" value={selectedRental?.emergencyContactNumber} />
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <InfoCard icon={AccessTimeIcon} title="Recent Activity">
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Latest Document
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {documents[0]?.name || 'No documents uploaded'}
              </Typography>
              {documents[0]?.upload_date && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(documents[0].upload_date).toLocaleDateString('en-GB')}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Next Invoice Due
              </Typography>
              {nextInvoiceDue ? (
                <>
                  <Typography variant="body1" fontWeight="500">
                    £{nextInvoiceDue.amount_due}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Due: {new Date(nextInvoiceDue.due_date).toLocaleDateString('en-GB')}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" fontWeight="500" color="success.main">
                  No outstanding invoices
                </Typography>
              )}
            </Box>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => document.getElementById('quick-upload').click()}
          >
            Upload Document
          </Button>
          <input
            id="quick-upload"
            type="file"
            hidden
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => setActiveTab(2)}
          >
            View Invoices
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhoneIcon />}
            href={`tel:${selectedRental?.emergencyContactNumber}`}
          >
            Call Emergency Contact
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  // Documents Tab
  const DocumentsTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="600">Documents</Typography>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => document.getElementById('document-upload').click()}
        >
          Upload Document
        </Button>
        <input
          id="document-upload"
          type="file"
          hidden
          onChange={handleFileUpload}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        />
      </Box>

      {documents.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Documents Yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Upload your first document to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => document.getElementById('document-upload').click()}
          >
            Upload Document
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Document Name</strong></TableCell>
                <TableCell><strong>Upload Date</strong></TableCell>
                <TableCell><strong>Reminder Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight="500">{doc.name}</Typography>
                        {doc.comment && (
                          <Typography variant="caption" color="text.secondary">
                            {doc.comment}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>
                    {doc.reminder_date ? new Date(doc.reminder_date).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={doc.status === 'ACCEPTED' ? <CheckCircleIcon /> : doc.status === 'DENIED' ? <WarningIcon /> : undefined}
                      label={doc.status}
                      color={
                        doc.status === 'ACCEPTED' ? 'success' :
                        doc.status === 'DENIED' ? 'error' :
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(doc.file_path, '_blank')}
                      title="View"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // Invoices Tab
  const InvoicesTab = () => (
    <Box>
      <Typography variant="h6" fontWeight="600" gutterBottom>Invoices</Typography>
      
      {invoices.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Invoices Yet</Typography>
          <Typography color="text.secondary">
            Your invoices will appear here when they're generated
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Invoice Date</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>
                    {new Date(invoice.invoice_date).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell>
                    {new Date(invoice.due_date).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="600">£{invoice.amount_due}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={invoice.status === 'PAID' ? <CheckCircleIcon /> : <AccessTimeIcon />}
                      label={invoice.status}
                      color={invoice.status === 'PAID' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(`/api/rentals/invoice/${invoice.id}`, '_blank')}
                      title="Download"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // Renter Management Tab (Admin only)
  const RenterManagement = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="600">Manage Renters</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditRenterData(null);
            setShowNewRenterDialog(true);
          }}
        >
          Add New Renter
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Salon</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Monthly Rent</strong></TableCell>
              <TableCell><strong>Start Date</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentals.map((rental) => (
              <TableRow key={rental.id} hover>
                <TableCell>
                  <Typography fontWeight="500">{rental.name}</Typography>
                </TableCell>
                <TableCell>{rental.address1}</TableCell>
                <TableCell>
                  <Chip 
                    label={rental.active ? 'Active' : 'Inactive'}
                    color={rental.active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight="600">£{rental.rentCost}</Typography>
                </TableCell>
                <TableCell>
                  {rental.dateRentalCommenced ? 
                    new Date(rental.dateRentalCommenced).toLocaleDateString('en-GB') : '-'}
                </TableCell>
                <TableCell>{rental.phoneNumber}</TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => handleEditRenter(rental)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Renter Dialog
  const RenterDialog = () => {
    const [formData, setFormData] = useState(
      editRenterData || {
        name: '',
        address1: '',
        address2: '',
        address3: '',
        postcode: '',
        phoneNumber: '',
        emergencyContactName: '',
        emergencyContactNumber: '',
        rentCost: '',
        rentPeriod: '',
        turnoverRentPercentage: '',
        dateRentalCommenced: '',
        invoiceOffset: '',
        active: true,
        id: null
      }
    );

    useEffect(() => {
      if (editRenterData) {
        setFormData({
          ...editRenterData,
          dateRentalCommenced: editRenterData.dateRentalCommenced ? 
            new Date(editRenterData.dateRentalCommenced).toISOString().split('T')[0] : '',
          rentCost: editRenterData.rentCost || '',
          turnoverRentPercentage: editRenterData.turnoverRentPercentage || '',
          invoiceOffset: editRenterData.invoiceOffset || ''
        });
      }
    }, [editRenterData]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };

    return (
      <Dialog 
        open={showNewRenterDialog} 
        onClose={() => {
          setShowNewRenterDialog(false);
          setEditRenterData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5' }}>
          {editRenterData ? 'Edit Renter' : 'Add New Renter'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Personal Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Emergency Contact Name"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Emergency Contact Number"
                name="emergencyContactNumber"
                value={formData.emergencyContactNumber}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Property Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Salon Name/Address"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address Line 2"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address Line 3"
                name="address3"
                value={formData.address3}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Rental Terms
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                label="Monthly Rent (£)"
                name="rentCost"
                type="number"
                value={formData.rentCost}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                label="Rent Period"
                name="rentPeriod"
                value={formData.rentPeriod}
                onChange={handleChange}
                placeholder="e.g., Monthly"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Turnover Rent %"
                name="turnoverRentPercentage"
                type="number"
                value={formData.turnoverRentPercentage}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                label="Start Date"
                name="dateRentalCommenced"
                type="date"
                value={formData.dateRentalCommenced}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Invoice Offset (days)"
                name="invoiceOffset"
                type="number"
                value={formData.invoiceOffset}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="active"
                  value={formData.active}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => {
            setShowNewRenterDialog(false);
            setEditRenterData(null);
          }}>
            Cancel
          </Button>
          <Button onClick={() => handleSaveRenter(formData)} variant="contained">
            {editRenterData ? 'Update' : 'Add'} Renter
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>Loading rental information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selectedRental) {
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <HomeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>No Rental Information Found</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {isAdmin 
            ? 'No contractors have been added yet.' 
            : 'Contact your administrator to set up your rental account.'}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditRenterData(null);
              setShowNewRenterDialog(true);
            }}
          >
            Add Independent Contractor
          </Button>
        )}
      </Paper>
      <RenterDialog />  {/* Don't forget this or the dialog won't render */}
    </Box>
  );
}

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700">
            {isAdmin ? 'Rental Management' : 'My Rental'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAdmin ? 'Manage all rental properties and Renters' : `Welcome back, ${selectedRental?.name || user?.email}`}
          </Typography>
        </Box>
        
        {isAdmin && rentals.length > 1 && (
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Select Renter</InputLabel>
            <Select
              value={selectedRental?.id || ''}
              onChange={handleRentalChange}
              label="Select Renter"
              size="small"
            >
              {rentals.map((rental) => (
                <MenuItem key={rental.id} value={rental.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {rental.name || rental.address1}
                    <Chip 
                      size="small"
                      label={rental.active ? 'Active' : 'Inactive'}
                      color={rental.active ? 'success' : 'error'}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAdmin && (
  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={() => {
      setEditRenterData(null);
      setShowNewRenterDialog(true);
    }}
  >
    Add Independent Contractor
  </Button>
)}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" icon={<HomeIcon />} iconPosition="start" />
          <Tab label="Documents" icon={<DescriptionIcon />} iconPosition="start" />
          <Tab label="Invoices" icon={<ReceiptIcon />} iconPosition="start" />
          {isAdmin && <Tab label="Manage Renters" icon={<PersonIcon />} iconPosition="start" />}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <RentalOverview />}
          {activeTab === 1 && <DocumentsTab />}
          {activeTab === 2 && <InvoicesTab />}
          {activeTab === 3 && isAdmin && <RenterManagement />}
        </Box>
      </Paper>

      <RenterDialog />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RentalDashboard;